/**
 * Help Side Sheet Component
 *
 * Purpose: Page-level help panel displayed as a right-side sheet
 * Created: March 24, 2026
 */

import React, { useState, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@xanectech/ui/components';
import { ScrollArea } from '@xanectech/ui/components';
import { Separator } from '@xanectech/ui/components';
import { HelpSearchBar } from './HelpSearchBar';
import { HelpArticleContent } from './HelpArticleContent';
import { HelpFeedback } from './HelpFeedback';
import { useHelpArticle, useHelpSearch, HelpService } from '@xanectech/data';
import { useQuery } from '@tanstack/react-query';
import { Book, ArrowLeft, FileText, FolderOpen } from 'lucide-react';
import { Button } from '@xanectech/ui/components';
import { HelpArticle, HelpCategory } from 'packages/types/src/general/help';

interface HelpSideSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  articleSlug: string | undefined;
  categorySlug?: string | undefined;
  helpType?: 'article' | 'category';
  onNavigate?: (slug: string) => void;
  app?: string;
}

export const HelpSideSheet: React.FC<HelpSideSheetProps> = ({
  open,
  onOpenChange,
  articleSlug,
  categorySlug,
  helpType = 'article',
  onNavigate,
  app,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [viewingArticle, setViewingArticle] = useState(false);

  const showCategory =
    helpType === 'category' &&
    !!categorySlug &&
    !viewingArticle &&
    !articleSlug;

  const { data: article, isLoading: articleLoading } = useHelpArticle(
    viewingArticle ? articleSlug : helpType === 'article' ? articleSlug : null,
  );

  const { data: searchResults } = useHelpSearch(searchQuery, app);

  const { data: categoryInfo } = useQuery({
    queryKey: ['help-category', categorySlug],
    queryFn: () => HelpService.getCategoryBySlug(categorySlug!),
    enabled: !!categorySlug && helpType === 'category',
    staleTime: 5 * 60 * 1000,
  });

  const { data: categoryArticles, isLoading: categoryLoading } = useQuery({
    queryKey: ['help-category-articles', categorySlug],
    queryFn: () => HelpService.getArticlesByCategorySlug(categorySlug!),
    enabled: !!categorySlug && helpType === 'category',
    staleTime: 5 * 60 * 1000,
  });

  const { data: relatedArticles } = useQuery({
    queryKey: ['help-related', article?.id],
    queryFn: () => HelpService.getRelatedArticles(article!.id as string),
    enabled: !!article?.id,
    staleTime: 5 * 60 * 1000,
  });

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleArticleClick = useCallback(
    (slug: string) => {
      setSearchQuery('');
      setViewingArticle(true);
      onNavigate?.(slug);
    },
    [onNavigate],
  );

  const handleBackToCategory = useCallback(() => {
    setViewingArticle(false);
    onNavigate?.('');
  }, [onNavigate]);

  // Reset viewingArticle when sheet closes or helpType changes
  React.useEffect(() => {
    if (!open) {
      setViewingArticle(false);
    }
  }, [open]);

  const isSearching = searchQuery.trim().length >= 2;
  const isShowingArticle = !showCategory && (articleSlug || viewingArticle);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side='right'
        className='w-full sm:max-w-lg p-0 flex flex-col'
      >
        <SheetHeader className='p-4 pb-0'>
          <div className='flex items-center gap-2'>
            {viewingArticle && helpType === 'category' && (
              <Button
                variant='ghost'
                size='icon'
                className='h-6 w-6 shrink-0'
                onClick={handleBackToCategory}
              >
                <ArrowLeft size={14} />
              </Button>
            )}
            <Book size={18} className='text-primary shrink-0' />
            <SheetTitle className='text-base'>
              {isShowingArticle
                ? (article?.title ?? 'Help')
                : (categoryInfo?.name ?? 'Help')}
            </SheetTitle>
          </div>
        </SheetHeader>

        <div className='px-4 py-3'>
          <HelpSearchBar onSearch={handleSearch} />
        </div>

        <Separator />

        <ScrollArea className='flex-1'>
          <div className='p-4'>
            {isSearching ? (
              <SearchResults
                results={
                  (searchResults ?? []) as Array<{
                    id: string;
                    slug: string;
                    title: string;
                    description: string | undefined;
                  }>
                }
                onSelect={handleArticleClick}
              />
            ) : showCategory ? (
              <CategoryArticleList
                articles={
                  (categoryArticles ?? []) as Array<{
                    id: string;
                    slug: string;
                    title: string;
                    description: string | undefined;
                    article_type: string | undefined;
                  }>
                }
                isLoading={categoryLoading}
                categoryDescription={categoryInfo?.description}
                onSelect={handleArticleClick}
              />
            ) : articleLoading ? (
              <div className='space-y-3'>
                <div className='h-4 bg-muted rounded animate-pulse w-3/4' />
                <div className='h-4 bg-muted rounded animate-pulse w-full' />
                <div className='h-4 bg-muted rounded animate-pulse w-2/3' />
              </div>
            ) : article ? (
              <>
                <HelpArticleContent content={article.content} />

                {relatedArticles && relatedArticles.length > 0 && (
                  <>
                    <Separator className='my-4' />
                    <h3 className='text-sm font-medium text-foreground mb-2'>
                      Related Articles
                    </h3>
                    <ul className='space-y-1'>
                      {relatedArticles.map((related) => (
                        <li key={related.id}>
                          <button
                            onClick={() => handleArticleClick(related.slug)}
                            className='text-sm text-primary hover:underline flex items-center gap-1.5'
                          >
                            <FileText size={12} />
                            {related.title}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                <Separator className='my-4' />
                <HelpFeedback articleId={article.id} />
              </>
            ) : (
              <div className='text-center py-8'>
                <Book
                  size={32}
                  className='mx-auto text-muted-foreground mb-3'
                />
                <p className='text-sm text-muted-foreground'>
                  No help article found for this page.
                </p>
                <p className='text-xs text-muted-foreground mt-1'>
                  Try searching for a topic above.
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

/** Category article list view */
const CategoryArticleList: React.FC<{
  articles: Array<{
    id: string;
    slug: string;
    title: string;
    description: string | undefined;
    article_type: string | undefined;
  }>;
  isLoading: boolean;
  categoryDescription?: string | undefined;
  onSelect: (slug: string) => void;
}> = ({ articles, isLoading, categoryDescription, onSelect }) => {
  if (isLoading) {
    return (
      <div className='space-y-3'>
        <div className='h-4 bg-muted rounded animate-pulse w-3/4' />
        <div className='h-12 bg-muted rounded animate-pulse w-full' />
        <div className='h-12 bg-muted rounded animate-pulse w-full' />
      </div>
    );
  }

  if (articles.length === 0) {
    return (
      <div className='text-center py-8'>
        <FolderOpen size={32} className='mx-auto text-muted-foreground mb-3' />
        <p className='text-sm text-muted-foreground'>
          No articles in this category yet.
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-3'>
      {categoryDescription && (
        <p className='text-sm text-muted-foreground mb-4'>
          {categoryDescription}
        </p>
      )}
      <ul className='space-y-1'>
        {articles.map((article) => (
          <li key={article.id}>
            <button
              onClick={() => onSelect(article.slug)}
              className='w-full text-left p-3 rounded-md hover:bg-muted/50 transition-colors'
            >
              <div className='flex items-start gap-2'>
                <FileText size={14} className='text-primary mt-0.5 shrink-0' />
                <div>
                  <p className='text-sm font-medium text-foreground'>
                    {article.title}
                  </p>
                  {article.description && (
                    <p className='text-xs text-muted-foreground mt-0.5 line-clamp-2'>
                      {article.description}
                    </p>
                  )}
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Internal search results list */
const SearchResults: React.FC<{
  results: Array<{
    id: string;
    slug: string;
    title: string;
    description: string | undefined;
  }>;
  onSelect: (slug: string) => void;
}> = ({ results, onSelect }) => {
  if (results.length === 0) {
    return (
      <p className='text-sm text-muted-foreground text-center py-6'>
        No results found.
      </p>
    );
  }

  return (
    <ul className='space-y-2'>
      {results.map((article) => (
        <li key={article.id}>
          <button
            onClick={() => onSelect(article.slug)}
            className='w-full text-left p-3 rounded-md hover:bg-muted/50 transition-colors'
          >
            <p className='text-sm font-medium text-foreground'>
              {article.title}
            </p>
            {article.description && (
              <p className='text-xs text-muted-foreground mt-0.5 line-clamp-2'>
                {article.description}
              </p>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
};
